import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  EMAIL_OTP_TYPES,
  exchangeCodeForSession,
  verifyTokenHash,
} from '@/server/auth';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';
import { POST_AUTH_REDIRECT } from '@/helpers/authRedirect';
import { safeRedirect } from '@/utils/safeRedirect';

const callbackSearchParams = z.object({
  code: z.string().optional(),
  token_hash: z.string().optional(),
  type: z.enum(EMAIL_OTP_TYPES).optional(),
  next: z.string().optional(),
});

export const Route = createFileRoute('/auth/callback')({
  validateSearch: callbackSearchParams,
  beforeLoad: async ({ search }) => {
    if (search.token_hash && search.type) {
      // Email confirmation flow: verify token hash directly (no PKCE cookie needed)
      const result = await verifyTokenHash({
        data: { tokenHash: search.token_hash, type: search.type },
      });
      if (result.error) {
        throw redirect({ to: '/login' });
      }
    } else if (search.code) {
      // OAuth flow: exchange PKCE code for session (same-session, cookie available)
      const result = await exchangeCodeForSession({
        data: { code: search.code },
      });
      if (result.error) {
        throw redirect({ to: '/login' });
      }
    }

    let destination = POST_AUTH_REDIRECT;
    let destinationSearch: Record<string, string> = {};
    if (search.next) {
      try {
        const url = new URL(search.next);
        destination = url.pathname;
        destinationSearch = Object.fromEntries(url.searchParams);
      } catch {
        destination = search.next;
      }
    }

    throw redirect({
      to: safeRedirect(destination, POST_AUTH_REDIRECT),
      search: destinationSearch,
    });
  },
  component: AuthCallbackFallback,
});

/**
 * Fallback spinner shown briefly while beforeLoad processes the code exchange.
 * In practice, the redirect in beforeLoad fires before this renders.
 */
function AuthCallbackFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <p className="text-sm text-slate-500">Signing you in...</p>
      </div>
    </div>
  );
}
